import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from "rxjs";
import {DjangoApiService} from "../../../shared/services/django-api.service";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {LanguageService} from "../../../shared/services/language.service";
import {LangChangeEvent, TranslateModule, TranslateService} from "@ngx-translate/core";
import {ActivatedRoute, Router} from "@angular/router";
import {CardsBasicComponent} from "../../../shared/components/cards/cards-basic/cards-basic.component";
import {FormsModule} from "@angular/forms";
import {MatButton} from "@angular/material/button";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {TableBasicComponent} from "../../../shared/components/tables/table-basic/table-basic.component";
import {PageEvent} from "@angular/material/paginator";

@Component({
  selector: 'app-allocation-detail',
  standalone: true,
  imports: [
    CardsBasicComponent,
    FormsModule,
    MatButton,
    MatFormField,
    MatInput,
    MatLabel,
    TranslateModule,
    TableBasicComponent
  ],
  templateUrl: './allocation-detail.component.html',
  styleUrl: './allocation-detail.component.css'
})
export class AllocationDetailComponent implements OnInit, OnDestroy {
  productId!: number;
  allocationId!: number;
  allocationData!: any;
  currentLanguage!: string;
  langChangeSubscription: Subscription | null = null;

  dataEntries: any[] = [];
  selectedRows: any[] = [];
  pageSize: number = 20;
  currentPage: number = 1;
  allocationEntryData: any[] = [];

  columnDef = [
    {key: 'date', header: 'Date'},
    {key: 'value', header: 'Value'},
    {key: 'translations.en.label', header: 'Label (EN)' },
  ];

  private subscriptions: Subscription = new Subscription();

  constructor(
    private _apiService: DjangoApiService,
    private _snackbarService: SnackbarService,
    public _languageService: LanguageService,
    private _translate: TranslateService,
    private _route: ActivatedRoute,
    private _router: Router) {
  }

  ngOnInit() {
    this.setupLanguageSubscription();
    this.getRouteParams();
  }

  private setupLanguageSubscription(): void {
    this.currentLanguage = this._languageService.getLanguage();
    this.subscriptions.add(
      this.langChangeSubscription = this._translate.onLangChange.subscribe((event: LangChangeEvent) => {
        this.currentLanguage = event.lang;
      })
    );
  }

  private getRouteParams() {
    this.subscriptions.add(
      this._route.params.subscribe(params => {
        this.productId = this._route.snapshot.params['productId'];
        this.allocationId = this._route.snapshot.params['allocationId'];
      })
    );

    if (!isNaN(Number(this.allocationId))) {
      this.loadAllocationData();
      this.loadAllocationEntryData();
    } else {
      this.createEmptyAllocation();
    }
  }

  private createEmptyAllocation() {
    const translations: { [key: string]: { title: string } } = {};

    this._languageService.supportedLangs.forEach(lang => {
      translations[lang] = {
        title: '',
      };
    });

    this.allocationData = {
      product: this.productId ? Number(this.productId): '',
      file_url: '',
      excel_sheet_name: '',
      file_product_id: '',
      file_category: '',
      file_type: '',
      translations: translations
    };
  }

  private loadAllocationData() {
    this.subscriptions.add(
      this._apiService.fetchAllocationDetail(this.allocationId).subscribe({
        next: (response: any) => {
          this.allocationData = response;
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  private loadAllocationEntryData() {
    this.subscriptions.add(
      this._apiService.fetchAllocationEntriesList(this.allocationId, this.currentPage, this.pageSize).subscribe({
        next: (response: any) => {
          this.allocationEntryData = response;
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  importAllocationFromUrl() {
    this.subscriptions.add(
      this._apiService.importAllocationFromUrl(this.allocationId).subscribe({
        next: (response: any) => {
          this._snackbarService.show('Prices successfully imported.');
        },
        error: (error: any) => {
          this._snackbarService.show('Error importing prices.');
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  private createAllocation() {
    this.subscriptions.add(
      this._apiService.createAllocation(this.allocationData).subscribe({
        next: (response: any) => {
          this.allocationData = response.data;
          this._snackbarService.show(response.message);
          this.updateUrl();
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  private updateAllocation() {
    this.subscriptions.add(
      this._apiService.updateAllocation(this.allocationId, this.allocationData).subscribe({
        next: (response: any) => {
          this.allocationData = response.data;
          this._snackbarService.show(response.message);
        },
        error: (error: any) => {
          console.error('There was an error fetching the user information', error);
        }
      })
    );
  }

  saveAlloaction() {
    this.allocationData.id ? this.updateAllocation() : this.createAllocation();
  }

  cancel() {
    this.allocationData.id ? this.loadAllocationData() : this.createEmptyAllocation();
  }

  private updateUrl(): void {
    this._router.navigateByUrl(this._router.url.replace(String('new-allocation'), String(this.allocationData.id)));
    this.productId = this.allocationData.id;
  }



  onPageChange(event: PageEvent) {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadAllocationEntryData();
  }

  onSelectionChange(selected: any[]) {
    this.selectedRows = selected;
  }


  ngOnDestroy() {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}
